import axios, { AxiosInstance } from 'axios';
import {
  getAuth, signOut, onAuthStateChanged, User,
} from 'firebase/auth';

let isForceLoggingOut = false;

const PERSISTED_STORE_KEY = 'beereaders-enrollment-admin';

// Max time to wait for Firebase to restore the session on boot before giving up.
const AUTH_RESTORE_TIMEOUT_MS = 2000;

function readPersistedState(): any {
  try {
    return JSON.parse(localStorage.getItem(PERSISTED_STORE_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

/**
 * Resolve the current Firebase user, waiting for Auth to be restored on boot.
 *
 * On a fresh page load Firebase has not yet rehydrated the session, so
 * `auth.currentUser` is null for a few hundred ms. Reading it synchronously
 * during that window made boot-time requests force a logout. If a persisted
 * session exists we wait (bounded by AUTH_RESTORE_TIMEOUT_MS) for the restore.
 */
function getCurrentUser(): Promise<User | null> {
  const auth = getAuth();

  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  const localStore = readPersistedState();
  if (!localStore.userToken || !localStore.userLogged) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(auth.currentUser), AUTH_RESTORE_TIMEOUT_MS);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      unsubscribe();
      resolve(user);
    });
  });
}

function writePersistedState(nextState: any): void {
  try {
    localStorage.setItem(PERSISTED_STORE_KEY, JSON.stringify(nextState || {}));
  } catch (error) {
    // ignore localStorage errors
  }
}

function setPersistedUserToken(userToken: string): void {
  const current = readPersistedState();
  writePersistedState({
    ...current,
    userToken,
  });
}

/**
 * Force logout: clear Vuex state, Firebase session, and redirect to /login.
 * Uses a flag to prevent multiple concurrent logouts when several requests
 * fail simultaneously with 401/403.
 */
function forceLogout(): void {
  if (isForceLoggingOut) return;
  isForceLoggingOut = true;

  // Clear persisted Vuex state so app boots unauthenticated.
  try {
    localStorage.removeItem(PERSISTED_STORE_KEY);
  } catch (error) {
    // ignore localStorage errors
  }

  const auth = getAuth();
  Promise.resolve(signOut(auth))
    .catch(() => null)
    .finally(() => {
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
        return;
      }

      isForceLoggingOut = false;
    });
}

export default class BaseService {
  baseUrl = '';

  private axiosInstance: AxiosInstance;

  constructor(baseRoute = '', baseUri = process.env.VUE_APP_CONTENT_MANAGER_API_URL) {
    this.baseUrl = `${baseUri}${baseRoute}`;

    // Create dedicated axios instance with proper token management
    this.axiosInstance = axios.create({
      headers: {
        'X-Client-ID': process.env.VUE_APP_CONTENT_MANAGER_CLIENT_ID ?? '',
      },
    });

    // Request interceptor: attach a fresh Firebase ID token.
    // Waits for Auth restoration on boot and uses getIdToken() (cached if still
    // valid, refreshed otherwise) instead of the possibly-expired persisted
    // token. Falls back to the persisted token only if no user is available.
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // eslint-disable-next-line no-param-reassign
        config.headers = config.headers || {};

        const user = await getCurrentUser();
        if (user) {
          try {
            const idToken = await user.getIdToken();
            setPersistedUserToken(idToken);
            // eslint-disable-next-line no-param-reassign
            config.headers.Authorization = `Bearer ${idToken}`;
            return config;
          } catch (error) {
            // fall through to the persisted token below
          }
        }

        const localStore = readPersistedState();
        if (localStore.userToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `Bearer ${localStore.userToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor: retry once with fresh token on 401/403, then force logout
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if ((status === 401 || status === 403) && !originalRequest?.retry) {
          originalRequest.retry = true;

          const user = await getCurrentUser();

          if (user) {
            try {
              const freshToken = await user.getIdToken(true);
              // Update localStorage so subsequent requests use the new token
              setPersistedUserToken(freshToken);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${freshToken}`;
              return this.axiosInstance.request(originalRequest);
            } catch (refreshError) {
              forceLogout();
              return Promise.reject(refreshError);
            }
          }

          // No Firebase user available -> force logout
          forceLogout();
          return Promise.reject(error);
        }

        return Promise.reject(error);
      },
    );
  }

  get = (url: string, params: any = {}) => this.axiosInstance.get(`${this.baseUrl}/${url}`, params);

  post = (url: string, data: any = {}) => this.axiosInstance.post(`${this.baseUrl}/${url}`, data);

  put = (url: string, data: any = {}) => this.axiosInstance.put(`${this.baseUrl}/${url}`, data);

  del = (url: string, data: any = {}) => this.axiosInstance.delete(`${this.baseUrl}/${url}`, { data });

  form = (url: string, data: any = {}, extraHeaders: Record<string, string> = {}) => {
    const formData = new FormData();

    formData.append('file', data);

    return this.axiosInstance.post(`${this.baseUrl}${url}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...extraHeaders },
    });
  };

  getFile = (url: string, params: any = {}) => this.axiosInstance.get(`${this.baseUrl}/${url}`, {
    ...params,
    responseType: 'blob',
  });

  bpost = (url: string, data: any = {}, params: any = {}) => this.axiosInstance.post(`${this.baseUrl}/${url}`, data, params);

  bput = (url: string, data: any = {}, params: any = {}) => this.axiosInstance.put(`${this.baseUrl}/${url}`, data, params);
}
